import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, ShoppingCart, DollarSign, LogOut } from 'lucide-react';
import { Product, Order } from '../types';
import { productApi, orderApi } from '../services/api';

interface AdminPageProps {
  onLogout: () => void;
}

const AdminPage: React.FC<AdminPageProps> = ({ onLogout }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'products' | 'orders'>('products');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    category: '',
    stock: '',
  });

  useEffect(() => {
    fetchData()
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsData, ordersData] = await Promise.all([
        productApi.getAll(),
        orderApi.getAll(),
      ]);
      setProducts(productsData);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const productData = {
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price),
        image: productForm.image,
        category: productForm.category,
        stock: parseInt(productForm.stock),
      };

      if (editingProduct) {
        await productApi.update(editingProduct.id, productData);
      } else {
        await productApi.create(productData);
      }

      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: '',
        image: '',
        category: '',
        stock: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image: product.image,
      category: product.category,
      stock: product.stock.toString(),
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productApi.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const totalRevenue = orders.reduce((sum, order) => sum + order.total, 0);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="h-24 bg-gray-300 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold tracking-wider" style={{ 
          background: 'linear-gradient(90deg, #39ff14 0%, #ff69b4 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontFamily: "'911 Porscha', sans-serif",
          fontStyle: 'italic'
        }}>ADMIN DASHBOARD</h1>
        <button
          onClick={onLogout}
          className="flex items-center px-4 py-2 rounded-md transition-colors font-medium border"
          style={{ borderColor: '#39ff14', color: '#e0e0e0' }}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Log Out
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="p-6 rounded-lg shadow-md border" style={{ backgroundColor: '#1a1a1a', borderColor: '#39ff14' }}>
          <div className="flex items-center">
            <Package className="h-8 w-8" style={{ color: '#39ff14' }} />
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: '#a0a0a0' }}>TOTAL PRODUCTS</p>
              <p className="text-2xl font-bold" style={{ color: '#39ff14' }}>{products.length}</p>
            </div>
          </div>
        </div>
        <div className="p-6 rounded-lg shadow-md border" style={{ backgroundColor: '#1a1a1a', borderColor: '#ff69b4' }}>
          <div className="flex items-center">
            <ShoppingCart className="h-8 w-8" style={{ color: '#ff69b4' }} />
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: '#a0a0a0' }}>TOTAL ORDERS</p>
              <p className="text-2xl font-bold" style={{ color: '#ff69b4' }}>{orders.length}</p>
            </div>
          </div>
        </div>
        <div className="p-6 rounded-lg shadow-md border" style={{ backgroundColor: '#1a1a1a', borderColor: '#bf00ff' }}>
          <div className="flex items-center">
            <DollarSign className="h-8 w-8" style={{ color: '#bf00ff' }} />
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: '#a0a0a0' }}>TOTAL REVENUE</p>
              <p className="text-2xl font-bold" style={{ color: '#bf00ff' }}>{formatPrice(totalRevenue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6" style={{ borderBottom: '1px solid #2a2a2a' }}>
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('products')}
            className="py-2 px-1 border-b-2 font-medium text-sm tracking-wider"
            style={{
              borderColor: activeTab === 'products' ? '#39ff14' : 'transparent',
              color: activeTab === 'products' ? '#39ff14' : '#a0a0a0'
            }}
          >
            PRODUCTS
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className="py-2 px-1 border-b-2 font-medium text-sm tracking-wider"
            style={{
              borderColor: activeTab === 'orders' ? '#39ff14' : 'transparent',
              color: activeTab === 'orders' ? '#39ff14' : '#a0a0a0'
            }}
          >
            ORDERS
          </button>
        </nav>
      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>PRODUCTS</h2>
            <button
              onClick={() => setShowProductForm(true)}
              className="flex items-center px-4 py-2 rounded-md transition-colors font-bold"
              style={{ backgroundColor: '#39ff14', color: '#0a0a0a' }}
            >
              <Plus className="h-4 w-4 mr-2" />
              ADD PRODUCT
            </button>
          </div>

          <div className="rounded-lg shadow-md overflow-hidden border" style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}>
            <table className="min-w-full" style={{ borderColor: '#2a2a2a' }}>
              <thead style={{ backgroundColor: '#0f0f0f' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: '#1a1a1a' }}>
                {products.map((product) => (
                  <tr key={product.id} style={{ borderTop: '1px solid #2a2a2a' }}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="h-10 w-10 rounded-md object-cover"
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium" style={{ color: '#e0e0e0' }}>{product.name}</div>
                          <div className="text-sm truncate max-w-xs" style={{ color: '#a0a0a0' }}>
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#e0e0e0' }}>
                      {product.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#39ff14' }}>
                      {formatPrice(product.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#e0e0e0' }}>
                      {product.stock}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="mr-3"
                        style={{ color: '#39ff14' }}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        style={{ color: '#ff4444' }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div>
          <h2 className="text-xl font-semibold mb-6 tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>ORDERS</h2>
          <div className="rounded-lg shadow-md overflow-hidden border" style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}>
            <table className="min-w-full">
              <thead style={{ backgroundColor: '#0f0f0f' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#39ff14' }}>
                    Date
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: '#1a1a1a' }}>
                {orders.map((order) => (
                  <tr key={order.id} style={{ borderTop: '1px solid #2a2a2a' }}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: '#e0e0e0' }}>
                      {order.id.slice(0, 8)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#e0e0e0' }}>
                      {order.customerInfo.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#39ff14' }}>
                      {formatPrice(order.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full" style={{
                        backgroundColor: order.status === 'delivered' ? '#39ff14' : 
                                       order.status === 'cancelled' ? '#ff4444' : '#2a2a2a',
                        color: order.status === 'delivered' ? '#0a0a0a' : 
                               order.status === 'cancelled' ? '#ffffff' : '#e0e0e0'
                      }}>
                        {order.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#e0e0e0' }}>
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Product Form Modal */}
      {showProductForm && (
        <div className="fixed inset-0 overflow-y-auto h-full w-full z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}>
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md" style={{ backgroundColor: '#1a1a1a', borderColor: '#39ff14' }}>
            <div className="mt-3">
              <h3 className="text-lg font-medium mb-4 tracking-wider" style={{ color: '#39ff14', fontFamily: "'911 Porscha', sans-serif", fontStyle: 'italic' }}>
                {editingProduct ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
              </h3>
              <form onSubmit={handleProductSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#e0e0e0' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
                    style={{ backgroundColor: '#0f0f0f', borderColor: '#39ff14', color: '#e0e0e0' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#e0e0e0' }}>
                    Description
                  </label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
                    style={{ backgroundColor: '#0f0f0f', borderColor: '#39ff14', color: '#e0e0e0' }}
                    rows={3}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#e0e0e0' }}>
                      Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
                      style={{ backgroundColor: '#0f0f0f', borderColor: '#39ff14', color: '#e0e0e0' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#e0e0e0' }}>
                      Stock
                    </label>
                    <input
                      type="number"
                      value={productForm.stock}
                      onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
                      style={{ backgroundColor: '#0f0f0f', borderColor: '#39ff14', color: '#e0e0e0' }}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#e0e0e0' }}>
                    Category
                  </label>
                  <input
                    type="text"
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
                    style={{ backgroundColor: '#0f0f0f', borderColor: '#39ff14', color: '#e0e0e0' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#e0e0e0' }}>
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={productForm.image}
                    onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
                    style={{ backgroundColor: '#0f0f0f', borderColor: '#39ff14', color: '#e0e0e0' }}
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowProductForm(false);
                      setEditingProduct(null);
                      setProductForm({
                        name: '',
                        description: '',
                        price: '',
                        image: '',
                        category: '',
                        stock: '',
                      });
                    }}
                    className="px-4 py-2 border rounded-md transition-colors font-medium"
                    style={{ borderColor: '#39ff14', color: '#e0e0e0', backgroundColor: 'transparent' }}
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-md transition-colors font-bold"
                    style={{ backgroundColor: '#39ff14', color: '#0a0a0a' }}
                  >
                    {editingProduct ? 'UPDATE' : 'CREATE'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
